/**
 * Admin Portal (web): "Clear ended free months for paying users" (2026-09-27).
 *
 * The owner's button for #4005's one-time backfill (J24): a user whose Summer50 /
 * strategic free month ended (or was revoked) and who then paid still carries
 * `free_month_ended` on their free-month row, so the chat guard and the funnel still
 * treat them as a lapsed free-month user. Preview first (counts, reasons, 8-character ids;
 * nothing written), then Apply (re-reads and converts who qualifies now; at most four
 * RevenueCat stamps a second). super_admin only — the server refuses everyone else (403).
 *
 * Self-contained on purpose: the Tier Management page mounts it with one line, next to the
 * cohort-labels panel. Backend: routers/admin_free_month_backfill.py. Decisions:
 * ./freeMonthBackfillLogic.ts. Web-only (the admin portal is not part of the native app).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Gift, AlertTriangle } from 'lucide-react';
import { AdminApiError, adminFetch } from '../../utils/adminApi';
import {
  applyConfirmText,
  applyTarget,
  keepPolling,
  latestPreview,
  qualifying,
  runLine,
  runningRun,
  skipRows,
  type BackfillRun,
} from './freeMonthBackfillLogic';

const BASE = '/api/admin/free-month-backfill';
const POLL_MS = 2000;

// Through the shared admin helper (utils/adminApi.ts: the session token, and no request
// at all without one), never a hand-rolled Authorization header (test_raw_bearer_ratchet).
async function call(path: string, init?: RequestInit): Promise<{ status: number; body: any }> {
  try {
    return { status: 200, body: await adminFetch<any>(`${BASE}${path}`, init) };
  } catch (err) {
    if (err instanceof AdminApiError) {
      let body: any = null;
      try {
        body = err.body ? JSON.parse(err.body) : null;
      } catch {
        body = null;
      }
      return { status: err.status, body };
    }
    // A non-JSON 200 is the SPA fallback page, not an answer: never read it as success.
    return { status: 502, body: null };
  }
}

function detailText(body: any, fallback: string): string {
  const d = body?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
}

export function FreeMonthBackfillPanel(): React.ReactElement {
  const [runs, setRuns] = useState<BackfillRun[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // False once unmounted: no poll may re-arm itself after the panel is gone.
  const mounted = useRef(true);
  // The last good read, so a failed read keeps polling a run that was in progress.
  const lastRuns = useRef<BackfillRun[]>([]);

  /** The runs, or null when the read failed (403 included). */
  const load = useCallback(async (): Promise<BackfillRun[] | null> => {
    const { status, body } = await call('/runs');
    if (!mounted.current) return null;
    if (status === 403) {
      setForbidden(true);
      return [];
    }
    if (status !== 200 || !body?.success || !Array.isArray(body.runs)) {
      setError('Could not read the runs.');
      return null;
    }
    setError(null);
    setRuns(body.runs);
    lastRuns.current = body.runs;
    return body.runs as BackfillRun[];
  }, []);

  // Poll while a run is in progress (the run executes on the server) — and through a
  // failed read while the last good read had one (no frozen spinner). One timer at a time,
  // and never after unmount.
  const poll = useCallback(async () => {
    const latest = await load();
    if (!mounted.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = keepPolling(latest, lastRuns.current) ? setTimeout(() => void poll(), POLL_MS) : null;
  }, [load]);

  useEffect(() => {
    mounted.current = true;
    void poll();
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [poll]);

  const start = async (path: string, body?: object) => {
    setBusy(true);
    setError(null);
    try {
      const res = await call(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (!mounted.current) return;
      if (res.status !== 200 || !res.body?.success) {
        setError(detailText(res.body, `The server answered ${res.status}.`));
      }
      await poll();
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const running = runningRun(runs);
  const preview = latestPreview(runs);
  const target = applyTarget(runs);
  const shown = running ?? runs[0] ?? null;

  const onApply = () => {
    if (!target.preview) return;
    const n = qualifying(target.preview);
    if (n <= 0 || !window.confirm(applyConfirmText(target.preview))) return;
    void start('/apply', { preview_run_id: target.preview.id });
  };

  const ids = preview?.status === 'done' ? (preview.summary?.target_ids8 ?? []) : [];

  return (
    <div className="mt-8 p-5 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 mb-1">
        <Gift className="h-5 w-5" style={{ color: '#1F6C4F' }} />
        <h2 className="text-lg font-semibold" style={{ color: '#0B1614' }}>Clear ended free months for paying users</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Users whose free month ended (or was revoked) and who paid before the purchase fix still read as a
        lapsed free month. This marks their free month as converted to paid and re-labels them in RevenueCat.
        Preview first — it writes nothing — then apply: the apply re-reads the closed free months and converts
        only who qualifies now.
      </p>

      {forbidden ? (
        <p className="text-sm text-gray-600">Only a super_admin can run this.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => void start('/preview')}
              disabled={busy || !!running}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Preview
            </button>
            <button
              onClick={onApply}
              disabled={busy || !target.preview}
              title={target.reason ?? undefined}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: '#1F6C4F' }}
            >
              {target.preview ? `Apply — re-read and convert (preview counted ${qualifying(target.preview)})` : 'Apply'}
            </button>
            {target.reason && !running && <span className="text-xs text-gray-500">{target.reason}</span>}
            {running && <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          )}

          {shown && <p className="text-sm text-gray-800 mb-3">{runLine(shown)}</p>}

          {preview && preview.status === 'done' && skipRows(preview).length > 0 && (
            <table className="w-full text-sm mb-3">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 font-medium">Not converted (latest preview)</th>
                  <th className="py-1 font-medium text-right">Users</th>
                </tr>
              </thead>
              <tbody>
                {skipRows(preview).map((row) => (
                  <tr key={row.reason} className="border-t border-gray-100">
                    <td className="py-1">{row.name}</td>
                    <td className="py-1 text-right">{row.users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {ids.length > 0 && (
            <p className="text-xs text-gray-500">
              Would convert (first 8 characters of each user id): <code>{ids.join(' ')}</code>
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default FreeMonthBackfillPanel;
